/**
 * What a marketing company earned, and what is still owed to them.
 *
 * Jonathon, 2026-08-28: Dot Edison advertises for the company and takes a
 * share of the profit on the work it produces. Jonathon, 2026-08-29, after
 * reading their Sales Enablement Agreement: the deal on paper is wider and
 * more particular than the first version of this file assumed, and every part
 * of it that could be argued about has to be a number somebody can point at.
 *
 * ## The two rates
 *
 * The agreement pays at two rates, and which one a job earns is about the
 * customer rather than the roof:
 *
 *   - **New connection** — the customer was not a relationship before. Their
 *     first sale. 15%.
 *   - **Existing connection** — new business from a customer we already knew,
 *     which is a second roof, or a customer who was already in Roof Board when
 *     the arrangement began. 7%.
 *
 * Neither number appears in this file. Both live on the arrangement, because
 * the agreement contradicts itself about the second one (7% on page 5, 5% on
 * page 8) and about what the percentage is taken of ("of the sale" on page 5,
 * "of gross profit" on page 8). When the two companies settle that in writing
 * it must be an edit to a row, not a change to this code.
 *
 * ## Who counts
 *
 * Two readings, and the arrangement says which it runs on:
 *
 *   - `lead_source` — only jobs carrying their lead source. Narrow, and what
 *     an advertiser with a tracking number would normally be paid on.
 *   - `all_customers` — every customer in the system who is not tagged
 *     No Contact, which is what the agreement actually says. Wide enough to
 *     include work the reps found themselves.
 *
 * The arrangement's `attribution` column picks the reading. Dot Edison ran on
 * the second from ADR 0075 until 2026-10-27, and has been on `lead_source`
 * since (ADR 0320). On the wide reading the report also counts, separately,
 * how much of the money came from their own advertising — the gap between
 * those two totals is the price of the clause — and the screen shows that
 * figure only on the wide reading.
 *
 * ## Five rules decide every figure, and all five were Jonathon's
 *
 *  1. **The share is of gross profit**, not AGP — revenue less what the job
 *     cost, before the job overhead. Which of the two a deal runs on is stored
 *     on the arrangement (`basis`), because they are thousands of dollars
 *     apart on a real roof.
 *  2. **Nothing is owed until the job is closed**, and nothing at all is owed
 *     until the arrangement has a start date. A signed job shows as pending so
 *     both sides can see it coming; the profit on a job still in production is
 *     a forecast, and paying a percentage of a forecast means clawing it back.
 *  3. **A customer tagged No Contact earns nobody anything.** The agreement
 *     lets the company hold a relationship out of the deal, and this is that
 *     tag. It is not `no_automated_contact`, which is the customer's own wish
 *     about email.
 *  4. **A losing job owes nothing, and never owes less than nothing.** A
 *     percentage of a negative profit is a credit, and no invoice works that
 *     way; the fee floors at zero.
 *  5. **Commission stops at the edges of the window.** Before the start date,
 *     or more than `tail_months` past the end, a job earns nothing however
 *     well it did.
 *
 * This is a vendor bill, not a commission. ADR 0025 deferred commissions and
 * still holds: nothing here is a rung of the ladder, `computeLadder` is
 * untouched, and the golden tests do not move. See ADR 0074 and ADR 0075.
 *
 * Money is only ever added up here, never in the screen (CLAUDE.md rule 3),
 * and the profit comes from `computeLadder` itself rather than a second sum
 * written in SQL — so this page and the estimate card can never disagree about
 * what a job made (rule 1).
 */

import { withinWindow } from "./callReport.js";
import { computeLadder, type LadderInput } from "./ladder.js";
import { isTestJob } from "./lost.js";
import {
  addCents,
  cents,
  percentOf,
  toCents,
  toPercent,
  type Cents,
} from "./money.js";
import type { DateFilter } from "./search.js";
import { SOLD_STAGES } from "./stage.js";
import type { JobStage } from "./types.js";

/** Which ladder figure a deal takes its percentage of. */
export type MarketingBasis = "gross_profit" | "agp";

/** Which sales belong to the arrangement. */
export type MarketingAttribution = "lead_source" | "all_customers";

/**
 * Which rate a job earns, or why it earns none.
 *
 * `excluded` and `outside_window` both pay zero and are kept apart because
 * they answer different questions: one is a decision somebody made about a
 * customer, the other is the calendar.
 */
export type MarketingTier =
  "new_connection" | "existing_connection" | "excluded" | "outside_window";

/** A row of `marketing_partners`, as the database wrote it. */
export interface MarketingPartner {
  id: string;
  name: string;
  lead_source: string | null;
  /** The new-connection rate, as a percentage: 15 means 15%. */
  fee_pct: unknown;
  /** The existing-connection rate, as a percentage. */
  repeat_fee_pct?: unknown;
  basis: MarketingBasis;
  attribution?: MarketingAttribution | null;
  /** NULL means the arrangement has not started and nothing is owed. */
  starts_on?: string | null;
  ended_on?: string | null;
  /** Months past `ended_on` that attributed customers keep earning. */
  tail_months?: number | null;
  agreement_note?: string | null;
  active?: boolean | null;
  notes?: string | null;
}

/** A row of `marketing_payments`. Always against a job. */
export interface MarketingPayment {
  id: string;
  partner_id: string;
  job_id: string;
  amount: unknown;
  paid_on: string;
  note?: string | null;
}

/** A job, as this report needs it. A `Job` satisfies it. */
export interface MarketingJob {
  id: string;
  job_number: number;
  job_name?: string | null;
  contact_id?: string | null;
  lead_source: string | null;
  stage: JobStage;
  created_at: string;
  job_type?: string | null;
  is_test?: boolean | null;
  /** Where the job is, which decides its permit fee (ADR 0289). */
  address?: string | null;
  city?: string | null;
  contact?: { full_name?: string | null; city?: string | null } | null;
}

/** A customer, as this report needs it. A `Contact` satisfies it. */
export interface MarketingContact {
  id: string;
  created_at?: string | null;
  /** Held out of the arrangement: earns nobody a percentage. */
  marketing_no_contact?: boolean | null;
  marketing_no_contact_at?: string | null;
}

/**
 * The one stage that means the money is in and the numbers have stopped
 * moving. `complete` is the work finished; `closed` is the file shut.
 */
export const EARNED_STAGE: JobStage = "closed";

/** Where a job sits in the arrangement, in the words the screen uses. */
export type FeeState = "earned" | "pending" | "open" | "lost";

export interface MarketingJobRow {
  jobId: string;
  jobNumber: number;
  /** Who the roof was for, for somebody scanning the list. */
  customer: string;
  stage: JobStage;
  createdAt: string;
  /** What the customer is paying. */
  contract: Cents;
  /** What the job cost us. */
  cost: Cents;
  /** The figure the percentage is taken of, per the arrangement's basis. */
  profit: Cents;
  /** Which rate this job earns, or why it earns none. */
  tier: MarketingTier;
  /** That rate as a percentage: 15 means 15%. Zero on a tier that pays none. */
  ratePct: number;
  /**
   * Their share, floored at zero. Owed only when `state` is `earned` — on a
   * pending job this is what it would come to if the numbers hold.
   */
  fee: Cents;
  state: FeeState;
  /**
   * Did their own advertising produce this one? Independent of `tier`: under
   * the wide reading a job earns whether or not it did, and the difference is
   * the whole reason to show the column.
   */
  fromTheirSource: boolean;
  /** Paid against this job so far. */
  paid: Cents;
  /** Still owed on this job. Zero until it is earned. */
  balance: Cents;
}

export interface MarketingTotals {
  /** Jobs the arrangement covers in the window, test jobs excluded. */
  leads: number;
  /** Of those, the ones that sold. */
  won: number;
  /**
   * Of those, the ones that are finished and settled. Counted off the stage
   * the job actually reached, not off whether a fee is owed — an arrangement
   * that has not started still has finished work behind it.
   */
  closed: number;
  /** Closed jobs earning the new-connection rate. */
  newConnections: number;
  /** Closed jobs earning the existing-connection rate. */
  existingConnections: number;
  /** Jobs in the window held out of the deal by a No Contact tag. */
  excluded: number;
  /** Contract dollars across the closed jobs. */
  contract: Cents;
  /**
   * Profit on the closed jobs — the figure a fee is taken of once the
   * arrangement is running. Real before it starts too, which is the point of
   * looking at this screen before signing.
   */
  profit: Cents;
  /** Owed on closed jobs. */
  jobFees: Cents;
  /**
   * Of `jobFees`, the part from jobs their own advertising produced. The rest
   * is what the agreement's wording adds on top of it.
   */
  sourceFees: Cents;
  /** Everything owed. The same figure as `jobFees`, under the name the screen
   * and the rest of the app use. */
  earned: Cents;
  /** What sold jobs not yet closed would come to. Not owed. */
  pending: Cents;
  /** Paid to them, against jobs in this window and against the arrangement. */
  paid: Cents;
  /** earned − paid. What is outstanding today. */
  balance: Cents;
}

export interface MarketingReport {
  partner: MarketingPartner | null;
  /** The new-connection rate as a fraction, for the wording on the screen. */
  feeRate: number;
  /** The existing-connection rate as a fraction. */
  repeatRate: number;
  basis: MarketingBasis;
  attribution: MarketingAttribution;
  /**
   * Has the arrangement started? False means no start date is recorded — every
   * figure is a forecast and nothing is owed.
   */
  started: boolean;
  rows: MarketingJobRow[];
  totals: MarketingTotals;
}

const emptyTotals = (): MarketingTotals => ({
  leads: 0,
  won: 0,
  closed: 0,
  newConnections: 0,
  existingConnections: 0,
  excluded: 0,
  contract: cents(0),
  profit: cents(0),
  jobFees: cents(0),
  sourceFees: cents(0),
  earned: cents(0),
  pending: cents(0),
  paid: cents(0),
  balance: cents(0),
});

/**
 * Does this job carry the partner's source?
 *
 * Compared case-insensitively and trimmed, because the value is free text on
 * the job (rule 8) and arrives from three places — the call webhook, the
 * new-lead form and the overview picker. "dot edison " and "Dot Edison" are the
 * same advertiser and must not be two rows on a bill.
 */
export function jobBelongsToPartner(
  job: Pick<MarketingJob, "lead_source">,
  partner: Pick<MarketingPartner, "lead_source" | "name"> | null,
): boolean {
  if (!partner) return false;
  const source = (partner.lead_source ?? partner.name ?? "").trim();
  if (!source) return false;
  return (job.lead_source ?? "").trim().toLowerCase() === source.toLowerCase();
}

/**
 * Is this job one the arrangement covers at all?
 *
 * Under `lead_source` that is the source test. Under `all_customers` it is
 * every job, because the agreement says every customer in the shared system
 * counts — the No Contact tag and the commission window are what take jobs
 * back out again, and they are applied separately so the screen can say which
 * of the two removed a job.
 */
export function jobIsCovered(
  job: Pick<MarketingJob, "lead_source">,
  partner: Pick<
    MarketingPartner,
    "lead_source" | "name" | "attribution"
  > | null,
): boolean {
  if (!partner) return false;
  if ((partner.attribution ?? "lead_source") === "all_customers") return true;
  return jobBelongsToPartner(job, partner);
}

/** A date-only string or timestamp as a day number, or null if unusable. */
function day(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(
    // A bare date means that whole day, wherever the reader is. Anchoring it
    // to UTC noon keeps a start date of the 1st from landing on the 31st for
    // somebody west of Greenwich, which would move a job across the boundary.
    /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00Z` : value,
  );
  return Number.isNaN(parsed) ? null : parsed;
}

/** The same instant, `months` later. */
function addMonths(at: number, months: number): number {
  const d = new Date(at);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.getTime();
}

/**
 * Is this job inside the commission window?
 *
 * Before the start date, nothing is earned: work that predates the arrangement
 * was not produced by it. After the end date plus the survival tail, nothing
 * is earned either — that is the twenty-four months the agreement gives them
 * on customers attributed before termination.
 *
 * With no start date recorded the window is open, because the arrangement has
 * not started and every figure on the screen is already a forecast.
 */
export function withinCommissionWindow(
  job: Pick<MarketingJob, "created_at">,
  partner: Pick<MarketingPartner, "starts_on" | "ended_on" | "tail_months">,
): boolean {
  const at = day(job.created_at);
  if (at === null) return false;

  const starts = day(partner.starts_on);
  if (starts !== null && at < starts) return false;

  const ends = day(partner.ended_on);
  if (ends !== null && at > addMonths(ends, partner.tail_months ?? 0)) {
    return false;
  }
  return true;
}

/**
 * For every customer, when their first sale was.
 *
 * Built from every job handed in, not the filtered ones: a customer's first
 * roof can easily be outside the date range somebody is looking at, and it
 * still means the second one is a repeat. Test jobs are ignored — no roof was
 * installed, so no relationship was established by one.
 */
export function firstSaleByContact(
  jobs: readonly MarketingJob[],
): Map<string, number> {
  const first = new Map<string, number>();
  for (const job of jobs) {
    if (isTestJob(job)) continue;
    if (!job.contact_id) continue;
    if (!SOLD_STAGES.includes(job.stage)) continue;
    const at = day(job.created_at);
    if (at === null) continue;
    const seen = first.get(job.contact_id);
    if (seen === undefined || at < seen) first.set(job.contact_id, at);
  }
  return first;
}

/**
 * Which rate this job earns.
 *
 * A customer is a *new connection* on the first business they ever gave us,
 * and an *existing connection* on everything after it — which is the
 * agreement's own two rows read plainly. A customer who was already in Roof
 * Board when the arrangement began is an existing connection from the start,
 * however many roofs they have bought: they were a relationship before the
 * advertiser arrived, and the agreement pays the lower rate on business from
 * relationships that already existed.
 *
 * Ties go to the customer being new. Two jobs created in the same instant is a
 * data accident, not a repeat sale.
 */
export function jobTier(
  job: MarketingJob,
  contact: MarketingContact | undefined,
  partner: Pick<
    MarketingPartner,
    "starts_on" | "ended_on" | "tail_months" | "attribution"
  >,
  firstSale: ReadonlyMap<string, number>,
): MarketingTier {
  // The calendar is asked first. A job outside the window has nothing to do
  // with this arrangement, and that is true whatever anybody tagged the
  // customer: asking about the tag first made No Contact *widen* the report,
  // dragging pre-agreement work into the closed-job totals it is supposed to
  // keep out.
  if (!withinCommissionWindow(job, partner)) return "outside_window";
  if (contact?.marketing_no_contact) return "excluded";

  const starts = day(partner.starts_on);
  const knownSince = day(contact?.created_at);
  if (starts !== null && knownSince !== null && knownSince < starts) {
    return "existing_connection";
  }

  const at = day(job.created_at);
  const theirFirst = job.contact_id ? firstSale.get(job.contact_id) : undefined;
  if (at !== null && theirFirst !== undefined && theirFirst < at) {
    return "existing_connection";
  }
  return "new_connection";
}

/** The percentage a tier earns. Zero on the two that earn nothing. */
export function tierRate(
  tier: MarketingTier,
  partner: Pick<MarketingPartner, "fee_pct" | "repeat_fee_pct">,
): number {
  if (tier === "new_connection") return toPercent(partner.fee_pct);
  if (tier === "existing_connection") return toPercent(partner.repeat_fee_pct);
  return 0;
}

/**
 * The profit a fee is taken of, added across every active estimate on the job.
 *
 * A job selling two options has two active estimates and made what both of them
 * made — the same rule `jobContractValues` follows for the contract amount.
 * `computeLadder` does the work, so this figure is the same one the estimate
 * card shows and cannot drift from it.
 */
export function jobProfit(
  inputs: readonly Omit<LadderInput, "jobType">[] | undefined,
  jobType: string | null | undefined,
  basis: MarketingBasis,
  place?: LadderInput["place"],
): { profit: Cents; contract: Cents; cost: Cents } {
  const ladders = (inputs ?? []).map((input) =>
    computeLadder({ ...input, jobType, place }),
  );
  return {
    profit: addCents(
      ...ladders.map((l) => (basis === "agp" ? l.agp : l.grossProfit)),
    ),
    contract: addCents(...ladders.map((l) => l.grandTotal)),
    cost: addCents(...ladders.map((l) => l.totalCost)),
  };
}

/**
 * Their share of a job's profit.
 *
 * Floored at zero: a job that lost money owes the advertiser nothing, and a
 * negative fee would quietly credit the company against the next job — which
 * is not a deal anybody struck.
 */
export function partnerFee(profit: Cents, feePct: unknown): Cents {
  if (profit <= 0) return cents(0);
  return percentOf(profit, toPercent(feePct));
}

/**
 * Where a job stands: is the fee owed, coming, still a maybe, or gone?
 *
 * `lost` and `open` both owe nothing, and they are kept apart because they say
 * different things to whoever is reading — one is a lead still being worked,
 * the other is a lead that went away.
 */
export function feeState(job: Pick<MarketingJob, "stage">): FeeState {
  if (job.stage === EARNED_STAGE) return "earned";
  if (job.stage === "lost") return "lost";
  return SOLD_STAGES.includes(job.stage) ? "pending" : "open";
}

/**
 * Everything the marketing screen shows, for one arrangement.
 *
 * `ladders` is what `fetchLadderInputs` returns, keyed by job id. `contacts`
 * is keyed by contact id and supplies two facts and no others: when we first
 * knew the customer, and whether they are held out of the deal.
 *
 * A job with no estimate yet makes nothing and owes nothing, which is the
 * honest answer for a lead nobody has priced. Test jobs are dropped: no roof
 * was installed, so no advertising sold one.
 */
export function marketingReport(
  partner: MarketingPartner | null,
  jobs: readonly MarketingJob[],
  ladders: ReadonlyMap<string, Omit<LadderInput, "jobType">[]>,
  payments: readonly MarketingPayment[],
  options: {
    filter?: DateFilter;
    now?: Date;
    contacts?: ReadonlyMap<string, MarketingContact>;
  } = {},
): MarketingReport {
  const filter = options.filter ?? "all";
  const now = options.now ?? new Date();
  const contacts = options.contacts ?? new Map<string, MarketingContact>();
  const basis: MarketingBasis = partner?.basis ?? "gross_profit";
  const attribution: MarketingAttribution =
    partner?.attribution ?? "lead_source";

  if (!partner) {
    return {
      partner: null,
      feeRate: 0,
      repeatRate: 0,
      basis,
      attribution,
      started: false,
      rows: [],
      totals: emptyTotals(),
    };
  }

  // No start date means the agreement is not in force. Every figure is still
  // worked out — that is the point of looking before signing — but nothing
  // reaches the "owed" column.
  const started = day(partner.starts_on) !== null;

  const paidByJob = new Map<string, Cents[]>();
  for (const payment of payments) {
    if (payment.partner_id !== partner.id) continue;
    const amount = toCents(payment.amount);
    const list = paidByJob.get(payment.job_id);
    if (list) list.push(amount);
    else paidByJob.set(payment.job_id, [amount]);
  }

  const firstSale = firstSaleByContact(jobs);

  const rows: MarketingJobRow[] = [];
  for (const job of jobs) {
    if (isTestJob(job)) continue;
    if (!jobIsCovered(job, partner)) continue;
    if (!withinWindow(job.created_at, filter, now)) continue;

    const contact = job.contact_id ? contacts.get(job.contact_id) : undefined;
    const tier = jobTier(job, contact, partner, firstSale);

    // Out of the commission window is out of the report: a job from before the
    // arrangement started is not a line somebody has to read past, it is work
    // this deal has nothing to do with. A No Contact customer stays, because
    // holding a customer out is a decision and the screen has to show that it
    // was made and what it saved.
    if (tier === "outside_window") continue;

    // Not started means no fee has been earned yet, whatever stage a job is
    // in. Shown as pending so the figure is visible and the balance is not.
    const reached = feeState(job);
    const state: FeeState =
      started || reached !== "earned" ? reached : "pending";

    const { profit, contract, cost } = jobProfit(
      ladders.get(job.id),
      job.job_type,
      basis,
      job,
    );
    const ratePct = tierRate(tier, partner);
    const fee = partnerFee(profit, ratePct);
    const paid = addCents(...(paidByJob.get(job.id) ?? []));

    rows.push({
      jobId: job.id,
      jobNumber: job.job_number,
      customer: job.contact?.full_name?.trim() || (job.job_name ?? ""),
      stage: job.stage,
      createdAt: job.created_at,
      contract,
      cost,
      profit,
      tier,
      ratePct,
      fee,
      state,
      fromTheirSource: jobBelongsToPartner(job, partner),
      paid,
      // Only an earned fee can be outstanding. A payment made early against a
      // job still in production shows as paid and leaves nothing owing, rather
      // than reading as a negative balance.
      balance: state === "earned" ? cents(Math.max(0, fee - paid)) : cents(0),
    });
  }

  // Money first: the job with the biggest outstanding balance is the reason
  // somebody opened this screen. Then newest, so the recent work is on top.
  rows.sort((a, b) => {
    if (b.balance !== a.balance) return b.balance - a.balance;
    return Date.parse(b.createdAt) - Date.parse(a.createdAt);
  });

  // Two different questions, and they came apart the moment an arrangement
  // could be un-started. `settled` is the work that is finished — the file is
  // shut and the numbers have stopped moving. `owed` is the money actually
  // due, which is nothing at all until the agreement starts.
  //
  // Before this split both were read off `state === "earned"`, and because an
  // un-started arrangement shows every earned job as pending, the screen
  // answered $0 contract and $0 profit under rows plainly showing a fee. That
  // is the state the seeded row ships in, so it was the first thing anybody
  // would have seen.
  const settled = rows.filter((r) => feeState(r) === "earned");
  const owed = rows.filter((r) => r.state === "earned");
  const jobFees = addCents(...owed.map((r) => r.fee));

  const totals: MarketingTotals = {
    leads: rows.length,
    won: rows.filter((r) => r.state === "earned" || r.state === "pending")
      .length,
    closed: settled.length,
    newConnections: settled.filter((r) => r.tier === "new_connection").length,
    existingConnections: settled.filter((r) => r.tier === "existing_connection")
      .length,
    excluded: rows.filter((r) => r.tier === "excluded").length,
    contract: addCents(...settled.map((r) => r.contract)),
    profit: addCents(...settled.map((r) => r.profit)),
    jobFees,
    sourceFees: addCents(
      ...owed.filter((r) => r.fromTheirSource).map((r) => r.fee),
    ),
    earned: jobFees,
    pending: addCents(
      ...rows.filter((r) => r.state === "pending").map((r) => r.fee),
    ),
    paid: addCents(...rows.map((r) => r.paid)),
    balance: cents(0),
  };
  // What is outstanding overall, not the sum of the per-job balances: an
  // overpayment on one job really does reduce what is owed in total, even
  // though it cannot make a single line negative.
  totals.balance = cents(Math.max(0, totals.earned - totals.paid));

  return {
    partner,
    feeRate: toPercent(partner.fee_pct) / 100,
    repeatRate: toPercent(partner.repeat_fee_pct) / 100,
    basis,
    attribution,
    started,
    rows,
    totals,
  };
}

const pct = (value: number): string =>
  Number.isInteger(value) ? String(value) : value.toFixed(2);

/**
 * How the arrangement reads in a sentence, both rates and the base:
 * "15% new / 7% repeat, of gross profit".
 */
export function dealSummary(
  partner: Pick<
    MarketingPartner,
    "fee_pct" | "repeat_fee_pct" | "basis"
  > | null,
): string {
  if (!partner) return "";
  const basis =
    partner.basis === "agp" ? "adjusted gross profit" : "gross profit";
  const first = toPercent(partner.fee_pct);
  const repeat = toPercent(partner.repeat_fee_pct);
  if (repeat > 0 && repeat !== first) {
    return `${pct(first)}% new / ${pct(repeat)}% repeat, of ${basis}`;
  }
  return `${pct(first)}% of ${basis}`;
}

/** What each state means, spelled out where a number alone would not. */
export const FEE_STATE_LABEL: Record<FeeState, string> = {
  earned: "Owed",
  pending: "Pending",
  open: "In progress",
  lost: "Lost",
};

/** What each tier means, in the agreement's own words where it has them. */
export const TIER_LABEL: Record<MarketingTier, string> = {
  new_connection: "New connection",
  existing_connection: "Existing connection",
  excluded: "No Contact",
  outside_window: "Outside the window",
};
